import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';
import { selectSprayEventsArray } from 'services/sprayEvents/selectors';
import { AppState } from 'services/types';
import { Person } from 'services/people/types';
import { selectPeopleArray } from 'services/people/selectors';
import { selectFertilizerEventsArray } from 'services/fertilizerEvents/selectors';
import { WorksheetType } from 'services/worksheet/types';
import {
    WORKSHEET_TYPE_FERTILIZER,
    WORKSHEET_TYPE_WEED_SPRAY
} from 'services/worksheet/constants';
import { OwnProps } from './index';
import { ApplicatorOptions } from './types';

const selectEventStartDate = (state: AppState, props: OwnProps): string =>
    props.eventStartDate;

const selectWorksheetType = (state: AppState, props: OwnProps): WorksheetType =>
    props.worksheetType;

const selectFertilizerEventsFilteredByStartDate = createDeepEqualSelector(
    selectFertilizerEventsArray,
    selectEventStartDate,
    (fertilizerEvents, startDate) =>
        fertilizerEvents.filter((event) => event.startDate === startDate)
);

const selectSprayEventsFilteredByStartDate = createDeepEqualSelector(
    selectSprayEventsArray,
    selectEventStartDate,
    (sprayEvents, startDate) =>
        sprayEvents.filter((event) => event.startDate === startDate)
);

const selectApplicatorPersonIds = createDeepEqualSelector(
    selectSprayEventsFilteredByStartDate,
    selectFertilizerEventsFilteredByStartDate,
    selectWorksheetType,
    (sprayEvents, fertilizerEvents, worksheetType) => {
        switch (worksheetType) {
            case WORKSHEET_TYPE_FERTILIZER:
                return fertilizerEvents.map(
                    (event) => event.fertilizerEvent.operatorPersonId
                );
            case WORKSHEET_TYPE_WEED_SPRAY: {
                const sprayEventOperations: number[] = [];
                sprayEvents
                    .filter((event) => event.sprayEvent.isWeedSpray)
                    .forEach((event) => {
                        event.sprayEvent.sprayEventOperations.forEach(
                            (operator) =>
                                sprayEventOperations.push(
                                    operator.operatorPersonId
                                )
                        );
                    });

                return sprayEventOperations;
            }
            default: {
                const sprayEventOperations: number[] = [];
                sprayEvents
                    .filter((event) => !event.sprayEvent.isWeedSpray)
                    .forEach((event) => {
                        event.sprayEvent.sprayEventOperations.forEach(
                            (operator) =>
                                sprayEventOperations.push(
                                    operator.operatorPersonId
                                )
                        );
                    });
                return sprayEventOperations;
            }
        }
    }
);

const selectPeopleByApplicatorPersonId = createDeepEqualSelector(
    selectPeopleArray,
    selectApplicatorPersonIds,
    (people: Person[], applicatorPersonIds: number[]) =>
        people.filter((person: Person) =>
            applicatorPersonIds.includes(person.id)
        )
);

const selectOptions = createDeepEqualSelector(
    selectPeopleByApplicatorPersonId,
    (applicators): ApplicatorOptions =>
        applicators.map((applicator) => {
            return {
                key: applicator.id,
                value: applicator.id,
                text: applicator.fullName
            };
        })
);

const makeSelectOptions = (): typeof selectOptions => selectOptions;

export {
    selectEventStartDate,
    selectSprayEventsFilteredByStartDate,
    selectApplicatorPersonIds,
    selectPeopleByApplicatorPersonId,
    selectOptions,
    makeSelectOptions
};
